import { AwsClient } from "aws4fetch";
import { getIdToken } from "../auth";
import {
  getOceanAwsRegion,
  getOceanCognitoIdentityPoolId,
  getOceanCognitoUserPoolId,
} from "./surfOceanConfig";

type CachedCreds = {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  expiresAt: number;
};

let cached: CachedCreds | null = null;

async function cognitoIdentityCall<T>(
  region: string,
  target: string,
  body: Record<string, unknown>,
): Promise<T> {
  const res = await fetch(`https://cognito-identity.${region}.amazonaws.com/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": `AWSCognitoIdentityService.${target}`,
    },
    body: JSON.stringify(body),
  });
  const json = (await res.json()) as T & { message?: string; __type?: string };
  if (!res.ok) {
    const msg = json.message || `Cognito Identity ${target} failed (${res.status})`;
    if (msg.includes("supported provider")) {
      throw new Error(
        `${msg} — il client OAuth SSO (web_oceany) deve essere registrato nell'identity pool Cognito ` +
          `(terraform apply su cloud/terraform/cognito.tf; vedi piano surf_sso_identity_pool_fix).`,
      );
    }
    throw new Error(msg);
  }
  return json;
}

function loginKey(region: string, userPoolId: string): string {
  return `cognito-idp.${region}.amazonaws.com/${userPoolId}`;
}

async function resolveCredentials(forceRefresh = false): Promise<CachedCreds> {
  const region = getOceanAwsRegion();
  const identityPoolId = getOceanCognitoIdentityPoolId();
  const userPoolId = getOceanCognitoUserPoolId();
  if (!region || !identityPoolId || !userPoolId) {
    throw new Error("Config Oceany incompleta (identity pool / region).");
  }
  if (!forceRefresh && cached && cached.expiresAt > Date.now() + 60_000) {
    return cached;
  }

  const idToken = await getIdToken();
  const logins = { [loginKey(region, userPoolId)]: idToken };
  const idRes = await cognitoIdentityCall<{ IdentityId: string }>(region, "GetId", {
    IdentityPoolId: identityPoolId,
    Logins: logins,
  });
  const creds = await cognitoIdentityCall<{
    Credentials: {
      AccessKeyId: string;
      SecretKey: string;
      SessionToken: string;
      Expiration: string;
    };
  }>(region, "GetCredentialsForIdentity", {
    IdentityId: idRes.IdentityId,
    Logins: logins,
  });
  const c = creds.Credentials;
  cached = {
    accessKeyId: c.AccessKeyId,
    secretAccessKey: c.SecretKey,
    sessionToken: c.SessionToken,
    expiresAt: new Date(c.Expiration).getTime(),
  };
  return cached;
}

export async function createOceanSignedFetch(): Promise<typeof fetch> {
  const region = getOceanAwsRegion();
  const creds = await resolveCredentials();
  const client = new AwsClient({
    accessKeyId: creds.accessKeyId,
    secretAccessKey: creds.secretAccessKey,
    sessionToken: creds.sessionToken,
    region,
    service: "lambda",
  });
  return (input: RequestInfo | URL, init?: RequestInit) => client.fetch(input, init);
}
